/**
 * Downscale a user-selected image before it is previewed or uploaded.
 *
 * A photo straight off a phone or DSLR is routinely 40–80 megapixels. Handing
 * that original to an `<img>` — even one painted into an 88×88 preview box —
 * forces the browser to decode the full-resolution bitmap and keep it in raster
 * memory (an 81MP image is ~324MB decoded). On a loaded tab that is enough to
 * push Chrome into discarding tiles, which paints large regions of the page
 * white until the next full repaint. It also means we PUT the untouched
 * original to S3, so a single pricebook item can cost tens of megabytes of
 * storage and a slow upload on site Wi-Fi.
 *
 * Resizing to a sane display size fixes all three: the preview is cheap and the
 * stored asset is small. The decode itself must be bounded too: decoding the
 * original and only then drawing it small still allocates the full bitmap and
 * can blank half the tab before the resize gets a chance to help.
 * `MAX_DIMENSION` is well
 * above every place we render a pricebook image today (the largest is the item
 * detail thumbnail), with headroom for retina and future larger renditions.
 *
 * This is best-effort by design: any failure (unsupported codec, canvas
 * tainting, an OOM inside `createImageBitmap`) returns the original file so the
 * user's upload still goes through. Shrinking is an optimisation, never a gate.
 */

/** Longest edge, in pixels, of the image we keep. */
export const MAX_DIMENSION = 1600;

/** Encoder quality for the lossy formats (JPEG/WebP). */
export const ENCODE_QUALITY = 0.85;

/**
 * Formats we can safely re-encode. GIF is excluded on purpose: a canvas
 * round-trip flattens an animated GIF to its first frame, which would silently
 * destroy the user's image.
 */
const RESIZABLE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

/**
 * Skip the round-trip for images that are already cheap. Small-but-tall images
 * (a 200×5000 banner) still get resized because the check is on dimensions;
 * this only avoids re-encoding a file that is small in both senses.
 */
const SKIP_RESIZE_BYTES = 256 * 1024;
const IMAGE_HEADER_BYTES = 256 * 1024;

export interface EncodedImageDimensions {
  width: number;
  height: number;
}

export function isResizableImageType(type: string): boolean {
  return RESIZABLE_TYPES.has(type);
}

/**
 * Scale factor to bring `width`×`height` under `MAX_DIMENSION`, or `1` when the
 * image already fits. Exported for tests — the sizing rule is the part worth
 * pinning down, the canvas plumbing around it is not.
 */
export function scaleToFit(width: number, height: number, max = MAX_DIMENSION): number {
  const longest = Math.max(width, height);
  if (longest <= max) return 1;
  return max / longest;
}

function ascii(view: DataView, offset: number, length: number): string {
  if (offset < 0 || offset + length > view.byteLength) return '';
  let value = '';
  for (let index = 0; index < length; index++)
    value += String.fromCharCode(view.getUint8(offset + index));
  return value;
}

function uint24le(view: DataView, offset: number): number {
  return (
    view.getUint8(offset) | (view.getUint8(offset + 1) << 8) | (view.getUint8(offset + 2) << 16)
  );
}

/**
 * Read dimensions from compressed image headers without allocating the image's
 * full raster. JPEG SOF markers normally occur in the first few kilobytes;
 * PNG and WebP keep dimensions in their fixed headers.
 */
export function encodedImageDimensions(
  buffer: ArrayBuffer,
  type: string,
): EncodedImageDimensions | null {
  const view = new DataView(buffer);

  if (type === 'image/png') {
    if (view.byteLength < 24 || ascii(view, 1, 3) !== 'PNG') return null;
    const width = view.getUint32(16);
    const height = view.getUint32(20);
    return width > 0 && height > 0 ? { width, height } : null;
  }

  if (type === 'image/webp') {
    if (view.byteLength < 30 || ascii(view, 0, 4) !== 'RIFF' || ascii(view, 8, 4) !== 'WEBP') {
      return null;
    }
    const chunk = ascii(view, 12, 4);
    if (chunk === 'VP8X') {
      return { width: uint24le(view, 24) + 1, height: uint24le(view, 27) + 1 };
    }
    if (chunk === 'VP8 ' && view.byteLength >= 30 && ascii(view, 23, 3) === '\u009d\u0001\u002a') {
      return {
        width: view.getUint16(26, true) & 0x3fff,
        height: view.getUint16(28, true) & 0x3fff,
      };
    }
    if (chunk === 'VP8L' && view.byteLength >= 25 && view.getUint8(20) === 0x2f) {
      const b1 = view.getUint8(21);
      const b2 = view.getUint8(22);
      const b3 = view.getUint8(23);
      const b4 = view.getUint8(24);
      return {
        width: 1 + b1 + ((b2 & 0x3f) << 8),
        height: 1 + ((b2 & 0xc0) >> 6) + (b3 << 2) + ((b4 & 0x0f) << 10),
      };
    }
    return null;
  }

  if (type === 'image/jpeg') {
    if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return null;
    let offset = 2;
    while (offset + 9 < view.byteLength) {
      if (view.getUint8(offset) !== 0xff) {
        offset++;
        continue;
      }
      while (offset < view.byteLength && view.getUint8(offset) === 0xff) offset++;
      if (offset >= view.byteLength) return null;
      const marker = view.getUint8(offset++);
      // Standalone markers have no length field.
      if (marker === 0xd8 || marker === 0xd9 || (marker >= 0xd0 && marker <= 0xd7)) continue;
      if (offset + 2 > view.byteLength) return null;
      const segmentLength = view.getUint16(offset);
      if (segmentLength < 2 || offset + segmentLength > view.byteLength) return null;
      const isStartOfFrame =
        marker >= 0xc0 && marker <= 0xcf && ![0xc4, 0xc8, 0xcc].includes(marker);
      if (isStartOfFrame && segmentLength >= 7) {
        const height = view.getUint16(offset + 3);
        const width = view.getUint16(offset + 5);
        return width > 0 && height > 0 ? { width, height } : null;
      }
      offset += segmentLength;
    }
  }

  return null;
}

export function imageBitmapResizeOptions(
  dimensions: EncodedImageDimensions | null,
): ImageBitmapOptions | undefined {
  if (!dimensions || scaleToFit(dimensions.width, dimensions.height) === 1) return undefined;
  // Specify one dimension only so the browser preserves aspect ratio. The
  // second canvas pass below handles EXIF rotations and any implementation
  // that returns a slightly different decoded size.
  return dimensions.width >= dimensions.height
    ? { resizeWidth: MAX_DIMENSION, resizeQuality: 'high' }
    : { resizeHeight: MAX_DIMENSION, resizeQuality: 'high' };
}

type ImageBitmapFactory = (
  image: ImageBitmapSource,
  options?: ImageBitmapOptions,
) => Promise<ImageBitmap>;

/**
 * Prefer a decode-time resize, but retain the canvas-only path for browsers
 * whose partial createImageBitmap implementation rejects resize options.
 */
export async function createImageBitmapWithResizeFallback(
  file: File,
  dimensions: EncodedImageDimensions | null,
  factory: ImageBitmapFactory = createImageBitmap,
): Promise<ImageBitmap> {
  const options = imageBitmapResizeOptions(dimensions);
  if (!options) return await factory(file);
  try {
    return await factory(file, options);
  } catch {
    return await factory(file);
  }
}

async function readEncodedDimensions(file: File): Promise<EncodedImageDimensions | null> {
  try {
    const header = await file.slice(0, IMAGE_HEADER_BYTES).arrayBuffer();
    return encodedImageDimensions(header, file.type);
  } catch {
    return null;
  }
}

async function decode(
  file: File,
  dimensions: EncodedImageDimensions | null,
): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    return await createImageBitmapWithResizeFallback(file, dimensions);
  }
  // jsdom and older Safari have no createImageBitmap — fall back to an <img>.
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to decode image'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function dimensionsOf(source: ImageBitmap | HTMLImageElement): { width: number; height: number } {
  return source instanceof HTMLImageElement
    ? { width: source.naturalWidth, height: source.naturalHeight }
    : { width: source.width, height: source.height };
}

/**
 * Returns a downscaled copy of `file`, or `file` itself when resizing is
 * unnecessary, unsupported, or fails. Never throws.
 */
export async function prepareImageForUpload(file: File): Promise<File> {
  if (!isResizableImageType(file.type)) return file;

  let source: ImageBitmap | HTMLImageElement;
  const encodedDimensions = await readEncodedDimensions(file);
  try {
    source = await decode(file, encodedDimensions);
  } catch {
    return file;
  }

  try {
    const { width, height } = dimensionsOf(source);
    if (!width || !height) return file;

    const scale = scaleToFit(width, height);
    const encodedImageWasOversized =
      encodedDimensions !== null &&
      scaleToFit(encodedDimensions.width, encodedDimensions.height) < 1;
    if (scale === 1 && file.size <= SKIP_RESIZE_BYTES && !encodedImageWasOversized) return file;

    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(source, 0, 0, targetWidth, targetHeight);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), file.type, ENCODE_QUALITY);
    });
    if (!blob) return file;
    // Re-encoding can come out larger than the original for an already-
    // optimised image. Keep the original only when it was already safe to
    // preview; returning a huge-dimension original would undo the bounded
    // decode and recreate the white-screen raster-pressure bug.
    if (blob.size >= file.size && !encodedImageWasOversized) return file;

    return new File([blob], file.name, { type: file.type, lastModified: file.lastModified });
  } catch {
    return file;
  } finally {
    if (!(source instanceof HTMLImageElement)) source.close();
  }
}
